// src/utils/imageUpload.js
// One place for all image uploads: resizes in the browser, uploads with a long
// cache lifetime and the correct content type.
import { supabase } from "../pages/supabaseClient";

const BUCKET = "images";
const CACHE_CONTROL = "31536000"; // 1 year (file names are unique, so this is safe)

const POST_MAX = 1280; // px, longest side of the main image
const POST_QUALITY = 0.8;
const THUMB_MAX = 640; // px, longest side of the card thumbnail
const THUMB_QUALITY = 0.75;
const AVATAR_SIZE = 192; // px, square
const AVATAR_QUALITY = 0.82;

const UPLOAD_OPTIONS = {
  upsert: false,
  cacheControl: CACHE_CONTROL,
  contentType: "image/jpeg",
};

const withTimeout = (promise, ms, message) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// Accepts a File/Blob, or any URL string (blob:, data:, http). Strings are
// fetched into a Blob first, which avoids canvas cross-origin problems.
async function toBlob(source) {
  if (source instanceof Blob) return source;
  const res = await fetch(source);
  return res.blob();
}

async function withImage(source, fn) {
  const blob = await toBlob(source);
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Couldn't read this image. Try a JPG or PNG."));
      i.src = url;
    });
    return await fn(img);
  } finally {
    URL.revokeObjectURL(url);
  }
}

const canvasToJpeg = (canvas, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Image conversion failed"))),
      "image/jpeg",
      quality
    )
  );

function newCanvas(w, h) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; // PNG transparency becomes white instead of black
  ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingQuality = "high";
  return { canvas, ctx };
}

// Scale down so the longest side is at most maxSide (never scales up)
function renderScaled(img, maxSide, quality) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const { canvas, ctx } = newCanvas(cw, ch);
  ctx.drawImage(img, 0, 0, cw, ch);
  return canvasToJpeg(canvas, quality);
}

// Center-crop to a square, then scale to size (never scales up)
function renderSquare(img, size, quality) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const side = Math.min(w, h);
  const sx = (w - side) / 2;
  const sy = (h - side) / 2;
  const out = Math.max(1, Math.min(size, side));
  const { canvas, ctx } = newCanvas(out, out);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
  return canvasToJpeg(canvas, quality);
}

const put = async (path, blob) => {
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, UPLOAD_OPTIONS);
  if (error) throw error;
};

async function removeQuietly(paths) {
  try {
    await supabase.storage.from(BUCKET).remove(paths);
  } catch (e) {
    /* best effort only */
  }
}

/**
 * Upload a post/idea image: a main version (<=1280px) and a thumbnail (<=640px).
 * Returns { filePath, thumbPath }. Save only `filePath` in Firestore (as `imagePath`);
 * the thumbnail path is derived from it by getThumbPath().
 */
export async function uploadPostImage(source, { uid, folder = "users", timeoutMs = 45000 } = {}) {
  if (!uid) throw new Error("User not authenticated");

  const base = `${folder}/${uid}/${Date.now()}_v2`;
  const filePath = `${base}.jpg`;
  const thumbPath = `${base}_thumb.jpg`;

  const [mainBlob, thumbBlob] = await withImage(source, async (img) => [
    await renderScaled(img, POST_MAX, POST_QUALITY),
    await renderScaled(img, THUMB_MAX, THUMB_QUALITY),
  ]);

  const results = await withTimeout(
    Promise.allSettled([put(filePath, mainBlob), put(thumbPath, thumbBlob)]),
    timeoutMs,
    "Upload timed out. Network slow or blocked."
  );

  const failed = results.find((r) => r.status === "rejected");
  if (failed) {
    // Never leave a main image without its thumbnail (or the other way round)
    const uploaded = [filePath, thumbPath].filter((_, i) => results[i].status === "fulfilled");
    if (uploaded.length) removeQuietly(uploaded);
    throw failed.reason;
  }

  return { filePath, thumbPath };
}

/**
 * Upload a profile picture: one small square image (192px).
 * Returns the storage path.
 */
export async function uploadAvatar(source, uid, { timeoutMs = 45000 } = {}) {
  if (!uid) throw new Error("User not authenticated");

  const filePath = `profile-pics/${uid}/${Date.now()}.jpg`;
  const blob = await withImage(source, (img) => renderSquare(img, AVATAR_SIZE, AVATAR_QUALITY));

  await withTimeout(put(filePath, blob), timeoutMs, "Upload timed out. Network slow or blocked.");
  return filePath;
}